import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

const ForgotPassword = () => {
  const { requestPasswordReset, authMode } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Enter your email.'); return; }
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setDone(true);
    } catch (err) {
      setError(err?.message || 'Could not request password reset.');
    } finally {
      setSubmitting(false);
    }
  };

  const ic = 'giwahs-auth-input mt-2';

  return (
    <div data-testid="forgot-password-page" className="surface-obsidian">
      <SEO title="Reset your password — GIWAHS" description="Request a password reset for your GIWAHS buyer account." noindex />
      <section className="border-b border-hairline">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-5">
            <span className="label-overline text-gold">/ Buyer Account</span>
            <h1 className="mt-4 font-display tracking-tighter text-platinum" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 0.95 }}>
              Reset password.
            </h1>
            <p className="mt-5 max-w-md text-platinum-2">
              Enter the email on your GIWAHS buyer account and we'll send a link to set a new password.
            </p>
            <div className="mt-8 border-t border-hairline pt-5 text-sm text-muted-warm">
              Remembered it? <Link to="/login" data-testid="forgot-to-login-link" className="underline underline-offset-4 text-platinum-2 hover:text-gold transition-colors">Back to sign in</Link>.
            </div>
          </div>

          <div className="md:col-span-7">
            {done ? (
              <div data-testid="forgot-success" className="giwahs-auth-panel p-6 md:p-10">
                <span className="label-overline text-gold">Check your inbox</span>
                <h2 className="mt-3 font-display text-2xl tracking-tight text-platinum">If an account exists for that email, a reset link is on its way.</h2>
                <p className="mt-3 text-sm text-platinum-2">Click the link in the email to set a new password. The link expires in 1 hour.</p>
                {authMode === 'mock' && (
                  <div className="giwahs-auth-notice mt-5 p-3 text-xs">
                    <span className="label-overline text-gold">Dev mode</span>
                    <p className="mt-1 text-platinum-2">Mock auth doesn't send real emails. Go to <Link to="/reset-password" className="underline hover:text-gold transition-colors">/reset-password</Link>, enter your email, and set the new password directly.</p>
                  </div>
                )}
                <Link to="/login" className="btn-giwahs btn-giwahs-secondary mt-6 inline-flex">Back to sign in</Link>
              </div>
            ) : (
              <form data-testid="forgot-form" onSubmit={submit} className="giwahs-auth-panel space-y-5 p-6 md:p-10">
                <div>
                  <label className="label-overline text-gold">Email</label>
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    autoComplete="email"
                    className={ic}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                {error && (
                  <div data-testid="forgot-error" role="alert" aria-live="polite" className="giwahs-auth-error px-3 py-2 text-sm">{error}</div>
                )}
                <button
                  data-testid="forgot-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="btn-giwahs btn-giwahs-primary w-full justify-center"
                >
                  {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Mail size={14} strokeWidth={1.5} /> Send reset link</>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForgotPassword;
